import { motion, useReducedMotion } from "framer-motion";
import {
  Building2, ClipboardCheck, LayoutDashboard, Megaphone, Plug, ShieldCheck, UserCog, Users2, Wallet,
} from "lucide-react";
import { NavLink } from "react-router-dom";
import { CONTROL } from "../../constants/testIds";
import { useKairooData } from "../../contexts/KairooDataContext";

// UX-CP-01 §1 "Fixed left sidebar... Nav items gated by the operator's Kairoo permissions
// (someone without billing doesn't see Monetization)". Dashboard/Gyms are the only two built
// this prompt — everything else routes to the shared StubPage (still fully wired, per the
// prompt's own "reused by UX-CP-02" instruction), same module-independence pattern as UX-01's
// gym sidebar (a permission the operator lacks simply removes the nav item, not a 403 screen).
const NAV_ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/kairoo/dashboard" },
  { key: "gyms", label: "Gyms", icon: Building2, path: "/kairoo/gyms", permission: "provision" },
  { key: "trials", label: "Applications", icon: ClipboardCheck, path: "/kairoo/trials", permission: "trial" },
  { key: "integrations", label: "Integrations", icon: Plug, path: "/kairoo/integrations", permission: "secrets" },
  { key: "monetization", label: "Monetization", icon: Wallet, path: "/kairoo/monetization", permission: "billing" },
  { key: "staff", label: "Kairoo Staff", icon: Users2, path: "/kairoo/staff", permission: "staff" },
  { key: "platform-ops", label: "Platform Ops", icon: ShieldCheck, path: "/kairoo/platform-ops", permission: "ops" },
  { key: "broadcast-os", label: "BroadcastOS", icon: Megaphone, path: "/kairoo/broadcast-os", permission: "broadcast" },
  { key: "emulator", label: "Emulator", icon: UserCog, path: "/kairoo/emulator", permission: "emulate" },
];

export function ControlSidebar() {
  const { me } = useKairooData();
  const reducedMotion = useReducedMotion();
  const permissions = me?.kairoo_permissions || [];
  const visibleItems = NAV_ITEMS.filter((item) => !item.permission || permissions.includes(item.permission));

  return (
    <aside data-testid={CONTROL.sidebar} className="scarlet-sidebar w-[240px] shrink-0 h-screen sticky top-0 flex flex-col">
      <div className="flex items-center gap-3 px-5 py-6">
        <div data-testid={CONTROL.logoMark} className="w-9 h-9 rounded-[10px] flex items-center justify-center text-white shrink-0" style={{ background: "#1F2430" }}>
          <ShieldCheck size={18} />
        </div>
        <div className="overflow-hidden">
          <p className="font-bold text-[15px] leading-tight truncate" style={{ color: "var(--scarlet-text)" }}>Kairoo</p>
          <p className="eyebrow leading-tight">Control Plane</p>
        </div>
      </div>

      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {visibleItems.map((item) => (
          <NavLink key={item.key} to={item.path} data-testid={CONTROL.navItem(item.key)} className="block">
            {({ isActive }) => (
              <div className="relative">
                {isActive && (
                  <motion.div
                    layoutId="control-active-nav-pill"
                    className="absolute inset-0 rounded-[10px]"
                    style={{ background: "var(--scarlet-accent)" }}
                    transition={reducedMotion ? { duration: 0 } : { duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  />
                )}
                <motion.div
                  whileHover={reducedMotion ? {} : { x: 2 }}
                  transition={{ duration: 0.14 }}
                  className="relative z-10 flex items-center gap-3 px-3 py-2.5 rounded-[10px] text-sm font-medium scarlet-focusable"
                  style={{ color: isActive ? "#FFFFFF" : "var(--scarlet-nav-text)" }}
                >
                  <item.icon size={18} strokeWidth={1.7} />
                  <span className={isActive ? "font-semibold" : ""}>{item.label}</span>
                </motion.div>
              </div>
            )}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}
